import { BRAND_IDS } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useOpsCustomerScopeContext } from '../api/customerScope.ts';
import { useT } from '../i18n/index.ts';
import { Chip, Select, Skeleton } from '../ui/index.ts';
import type { SelectOption } from '../ui/index.ts';
import { brandLabel } from './BrandChip.tsx';

/**
 * Server-determined brand context for the sales surfaces (epic #1030
 * E1-S4, #1040). Pool / workbench / ownership mount this next to their
 * page header:
 *
 *   - sales sessions see the profile brand plus whether the book is
 *     self-scoped (customer manager) or brand-wide (sales lead) — the
 *     values come from `GET /v1/ops/customers/context`, never from
 *     client-side state;
 *   - the pool page is the exception (`poolView`) only for a self-scoped
 *     customer manager: their book is ownership-defined, so the pool is
 *     brand-blind (#1540 decision 3) and they get the same cross-brand
 *     chip and (when the page wires `onBrandFilterChange`) brand filter
 *     that admin gets. The filter only narrows. A sales lead's book is
 *     still the brand pin, so on the pool they keep the profile-brand
 *     chip and no filter. Workbench / ownership / 360 leave `poolView`
 *     off and keep the profile-brand chip;
 *   - admin sees a cross-brand chip and (when the page wires
 *     `brandFilter` / `onBrandFilterChange`) a brand filter select;
 *   - finance (#1958) sees the same cross-brand chip without a filter: the
 *     reads are unscoped, but the session works one customer at a time;
 *   - a sales user without a profile sees the fail-closed hint that the
 *     scoped list routes will also return.
 */

/** Radix Select forbids empty-string values; "all brands" travels as this. */
const ALL_BRANDS_SENTINEL = 'all';

export function BrandScopeBanner({
  testIdPrefix,
  brandFilter,
  onBrandFilterChange,
  poolView = false,
}: {
  readonly testIdPrefix: string;
  /**
   * Current brand filter ('' = all brands). On the pool it narrows every
   * session; elsewhere only admin reads it.
   */
  readonly brandFilter?: string;
  readonly onBrandFilterChange?: (brand: string) => void;
  /**
   * Pool page only. When true, a self-scoped customer manager renders
   * the cross-brand chip and the wired brand filter (their pool is
   * brand-blind). A sales lead keeps the profile-brand chip. Default
   * false keeps workbench / ownership / 360 on the profile-brand chip.
   */
  readonly poolView?: boolean;
}): JSX.Element | null {
  const t = useT();
  const context = useOpsCustomerScopeContext();

  if (context.isPending) {
    return (
      <Skeleton
        className="h-6 w-40 rounded-full"
        testId={`${testIdPrefix}-brand-scope-loading`}
      />
    );
  }

  if (context.isError) {
    if (context.error instanceof OpsApiError && context.error.code === 'FORBIDDEN') {
      return (
        <Chip tone="warn" testId={`${testIdPrefix}-brand-scope-no-profile`}>
          {t('brand_scope.no_profile')}
        </Chip>
      );
    }
    return null;
  }

  const scope = context.data.scope;
  // Pool (#1540 decision 3): a self-scoped customer manager sees every
  // brand's unassigned customers, so they share the admin chrome. A
  // sales lead's book is still the brand pin, so they fall through to
  // the profile-brand chip. The chip testId stays distinct so a page
  // test can tell a pool customer-manager session from an admin session.
  if (scope.kind === 'admin' || (poolView && scope.kind === 'sales' && scope.self_scoped)) {
    const filterWired = onBrandFilterChange !== undefined;
    const options: readonly SelectOption[] = [
      { value: ALL_BRANDS_SENTINEL, label: t('brand_scope.filter.all') },
      ...BRAND_IDS.map((brand) => ({ value: brand, label: brandLabel(brand) })),
    ];
    const chipTestId =
      scope.kind === 'admin'
        ? `${testIdPrefix}-brand-scope-admin`
        : `${testIdPrefix}-brand-scope-pool`;
    return (
      <div className="flex items-center gap-2 max-md:flex-wrap">
        <Chip tone="neutral" testId={chipTestId}>
          {t('brand_scope.cross_brand')}
        </Chip>
        {filterWired ? (
          <Select
            value={
              brandFilter !== undefined && brandFilter.length > 0
                ? brandFilter
                : ALL_BRANDS_SENTINEL
            }
            onValueChange={(value) =>
              onBrandFilterChange(value === ALL_BRANDS_SENTINEL ? '' : value)
            }
            options={options}
            ariaLabel={t('brand_scope.filter.aria_label')}
            testId={`${testIdPrefix}-brand-filter`}
            className="w-40"
          />
        ) : null}
      </div>
    );
  }

  // Finance session (#1958): cross-brand like admin, but read-only — no brand
  // filter, because a finance manager reaches these reads to check the customer
  // behind a credit application, not to work a book.
  if (scope.kind === 'finance') {
    return (
      <Chip tone="neutral" testId={`${testIdPrefix}-brand-scope-finance`}>
        {t('brand_scope.cross_brand')}
      </Chip>
    );
  }

  // Sales session: the brand is the server-side profile brand (never null
  // for kind === 'sales'; guard defensively for the wire shape anyway).
  const label =
    scope.brand === null
      ? t('brand_scope.cross_brand')
      : scope.self_scoped
        ? t('brand_scope.self_scoped', { brand: brandLabel(scope.brand) })
        : t('brand_scope.brand_wide', { brand: brandLabel(scope.brand) });
  return (
    <Chip tone="signal" testId={`${testIdPrefix}-brand-scope-sales`}>
      {label}
    </Chip>
  );
}
